import { useEffect, useState } from "react";
import { Formik, getIn } from "formik";
import { Button, Card, Col, Row, Spinner } from "reactstrap";
import { validationSchema } from "../../../validations/party.validation";
import FormInput from "../../../components/FormInput";
import { addParty, getParty, updateParty } from "../../../services/parties.service";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import QueryString from "qs";
import { toast } from "react-hot-toast";
import { getTransporters } from "../../../services/transporters.service";
import FormSelect from "../../../components/FormSelect";
import { getStates } from "../../../services/state.service";
import { getCities } from "../../../services/city.service";

interface State {
  name: string;
}

interface Transporter {
  id: string;
}

interface InitialValues {
  type: any;
  categoryType: any;
  name: string;
  code: string;
  gstNumber: string;
  mobileNumber: string;
  state: State | undefined;
  city: State | undefined;
  address: string;
  transporter: Transporter | undefined;
}

const initialValues: InitialValues = {
  type: { id: "Other", name: "Other" },
  categoryType: { id: "Retailer", name: "Retailer" },
  name: "",
  code: "",
  gstNumber: "",
  mobileNumber: "",
  state: undefined,
  city: undefined,
  address: "",
  transporter: undefined,
};

export default function Form() {
  const location = useLocation();
  const navigate = useNavigate();
  const params: any = useParams();
  const [initialValuesData, setInitialValuesData] = useState(initialValues);
  const [loader, setLoader] = useState(true);
  const query: any = QueryString.parse(location.search, { ignoreQueryPrefix: true });

  useEffect(() => {
    if (params.id) {
      getParty(params.id)
        .then((response) => {
          const partyData = response.data;

          // Update the initialValues object with the received data
          const updatedInitialValues = {
            ...initialValues,
            type: { id: partyData.type, name: partyData.type } || { id: "Other", name: "Other" },
            categoryType: { id: partyData.categoryType, name: partyData.categoryType } || {
              id: "Retailer",
              name: "Retailer",
            },
            name: partyData.name || "",
            code: partyData.code || "",
            gstNumber: partyData.gstNumber || "",
            mobileNumber: partyData.mobileNumber || "",
            state: partyData.state || undefined,
            city: partyData.city || undefined,
            address: partyData.address || "",
            transporter: partyData.transporter || undefined,
          };

          setInitialValuesData(updatedInitialValues);
          setLoader(false);
        })
        .catch((error) => {
          // Handle error if API call fails
          toast.error(`Error fetching party data: ${JSON.stringify(error)}`);
        });
    } else {
      setLoader(false);
    }
  }, [params.id]);

  function handleSubmit(values: any, actions: any) {
    const updatedData: any = {
      ...values,
      city: values.city.id,
      state: values.state.id,
      transporter: values.transporter.id,
      type: values.type.id,
      categoryType: values.categoryType.id,
    };
    if (params.id) {
      updateParty(params.id, updatedData)
        .then((response) => {
          toast.success(response.data.message);
          navigate(query.redirect);
        })
        .catch(() => {
          actions.setSubmitting(false);
        });
    } else {
      addParty(updatedData)
        .then((response) => {
          toast.success(response.data.message);
          navigate(query.redirect);
        })
        .catch(() => {
          actions.setSubmitting(false);
        });
    }
  }
  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      {loader ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={initialValuesData}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleBlur, handleChange, setFieldValue, handleSubmit }) => (
            <>
              <Row className='align-items-center mb-2 page-title-row'>
                <Col sm>
                  <h1 className='my-1 fw-bold h4'>Party Master</h1>
                </Col>
                <Col sm='auto'>
                  <div className='justify-content-between'>
                    <Button
                      size='sm'
                      color='secondary'
                      className='my-1 me-2 fw-semibold'
                      style={{ opacity: 1 }}
                      onClick={() => {
                        navigate(query.redirect);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size='sm'
                      color='primary'
                      className='my-1 text-white'
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </Button>
                  </div>
                </Col>
              </Row>
              <div className='flex-grow-1 overflow-auto'>
                <Card className='h-100 p-3 w-100 '>
                  <Col md={8}>
                    <div className='d-flex flex-column h-100 rounded-0'>
                      <form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormSelect
                              id='type'
                              name='type'
                              label='Type'
                              options={[
                                { name: "Other", id: "Other" },
                                { name: "Local", id: "Local" },
                              ]}
                              onChange={(name: any, value: any) => {
                                setFieldValue(name, value);
                              }}
                              value={getIn(values, "type")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormSelect
                              id='categoryType'
                              name='categoryType'
                              label='Category Type'
                              options={[
                                { name: "Retailer", id: "Retailer" },
                                { name: "ANC", id: "ANC" },
                              ]}
                              onChange={(name: any, value: any) => {
                                setFieldValue(name, value);
                              }}
                              value={getIn(values, "categoryType")}
                            />
                          </Col>

                          <Col md={6}>
                            <FormInput
                              name='name'
                              label='FullName'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={getIn(values, "name")}
                              error={getIn(errors, "name")}
                              touched={getIn(touched, "name")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='code'
                              label='Party Code'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={getIn(values, "code")}
                              error={getIn(errors, "code")}
                              touched={getIn(touched, "code")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='gstNumber'
                              label='GST Number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={getIn(values, "gstNumber")}
                              error={getIn(errors, "gstNumber")}
                              touched={getIn(touched, "gstNumber")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='mobileNumber'
                              label='Mobile Number'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={getIn(values, "mobileNumber")}
                              error={getIn(errors, "mobileNumber")}
                              touched={getIn(touched, "mobileNumber")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormSelect
                              id='state'
                              name='state'
                              label='State'
                              isAsync
                              getOptions={getStates}
                              onChange={(name: any, value: any) => {
                                setFieldValue(name, value);
                                setFieldValue("city", "");
                              }}
                              onBlur={handleBlur}
                              value={getIn(values, "state")}
                              error={getIn(errors, "state")}
                              touched={getIn(touched, "state")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormSelect
                              id='city'
                              name='city'
                              key={getIn(values, "state.id")}
                              label='City'
                              isAsync
                              getOptions={getCities}
                              defaultFilter={{ state: getIn(values, "state.id") || "" }}
                              onChange={(name: any, value: any) => {
                                setFieldValue(name, value);
                              }}
                              onBlur={handleBlur}
                              value={getIn(values, "city")}
                              error={getIn(errors, "city")}
                              touched={getIn(touched, "city")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormInput
                              name='address'
                              label='Address'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={getIn(values, "address")}
                              error={getIn(errors, "address")}
                              touched={getIn(touched, "address")}
                            />
                          </Col>
                          <Col md={6}>
                            <FormSelect
                              id='transporter'
                              name='transporter'
                              label='Transporter'
                              isAsync
                              getOptions={getTransporters}
                              defaultFilter={{ status: true }}
                              onChange={(name: any, value: any) => {
                                setFieldValue(name, value);
                              }}
                              onBlur={handleBlur}
                              value={getIn(values, "transporter")}
                              error={getIn(errors, "transporter")}
                              touched={getIn(touched, "transporter")}
                            />
                          </Col>
                        </Row>
                      </form>
                    </div>
                  </Col>
                </Card>
              </div>
            </>
          )}
        </Formik>
      )}
    </div>
  );
}
